import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { toast } from "sonner";
import { 
  GripVertical, 
  ArrowRight,
  Plus,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Clock,
  Zap,
  Target,
  Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_PHOTOS = {
  sfh: "https://images.pexels.com/photos/4469150/pexels-photo-4469150.jpeg?auto=compress&cs=tinysrgb&w=400",
  mf: "https://images.unsplash.com/photo-1664833189203-cbd564954b11?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  condo: "https://images.pexels.com/photos/5563473/pexels-photo-5563473.jpeg?auto=compress&cs=tinysrgb&w=400",
  commercial: "https://images.pexels.com/photos/36237028/pexels-photo-36237028.jpeg?auto=compress&cs=tinysrgb&w=400",
  land: "https://images.unsplash.com/photo-1716077521105-f0d699ab2047?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  default: "https://images.pexels.com/photos/4469150/pexels-photo-4469150.jpeg?auto=compress&cs=tinysrgb&w=400",
};
function getPropertyPhoto(type) {
  if (!type) return PROPERTY_PHOTOS.default;
  const t = type.toLowerCase();
  if (t === "sfh" || t.includes("single")) return PROPERTY_PHOTOS.sfh;
  if (t === "mf" || t.includes("multi")) return PROPERTY_PHOTOS.mf;
  if (t.includes("condo")) return PROPERTY_PHOTOS.condo;
  if (t.includes("commercial")) return PROPERTY_PHOTOS.commercial;
  if (t.includes("land")) return PROPERTY_PHOTOS.land;
  return PROPERTY_PHOTOS.default;
}

// Full Transaction Lifecycle Stages with colors
const STAGES = [
  // Acquisition Phase
  { key: "new_lead", label: "New Lead", color: "#3B82F6", phase: "acquisition" },
  { key: "analyzed", label: "Analyzed", color: "#6366F1", phase: "acquisition" },
  { key: "underwritten", label: "Underwritten", color: "#8B5CF6", phase: "acquisition" },
  { key: "contracted", label: "Contracted", color: "#D4AF37", phase: "acquisition" },
  // Processing Phase
  { key: "rehab_estimate", label: "Rehab Estimate", color: "#F97316", phase: "processing" },
  { key: "sent_to_title", label: "Sent to Title", color: "#06B6D4", phase: "processing" },
  { key: "title_processing", label: "Title Processing", color: "#14B8A6", phase: "processing" },
  { key: "ready_to_close", label: "Ready to Close", color: "#84CC16", phase: "processing" },
  // Closed Phase
  { key: "closed", label: "Closed", color: "#10B981", phase: "closed" },
  // Disposition Phase
  { key: "posting_contractors", label: "Posting Contractors", color: "#F43F5E", phase: "disposition" },
  { key: "contractor_selected", label: "Contractor Selected", color: "#EC4899", phase: "disposition" },
  { key: "finding_realtor", label: "Finding Realtor", color: "#D946EF", phase: "disposition" },
  { key: "listed", label: "Listed", color: "#A855F7", phase: "disposition" },
  { key: "under_contract_sale", label: "Under Contract", color: "#6366F1", phase: "disposition" },
  { key: "sold", label: "Sold", color: "#22C55E", phase: "final" },
  // Terminal
  { key: "dead", label: "Dead", color: "#71717A", phase: "terminal" }
];

const PHASE_CONFIG = {
  acquisition: { label: 'ACQUISITION', color: '#3B82F6' },
  processing: { label: 'PROCESSING', color: '#06B6D4' },
  closed: { label: 'CLOSED', color: '#D4AF37' },
  disposition: { label: 'DISPOSITION', color: '#F43F5E' },
  final: { label: 'FINAL', color: '#22C55E' },
  terminal: { label: 'TERMINAL', color: '#71717A' }
};

export default function PipelinePage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { mainClass } = useSidebar();
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchDeals();
  }, []);

  // Canvas animation for flow visualization
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || deals.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / Math.min(STAGES.length, 8)),
        y: canvas.height / 2 + (Math.random() - 0.5) * 30,
        targetX: (stageIdx + 1.5) * (canvas.width / Math.min(STAGES.length, 8)),
        progress: 0,
        speed: 0.004 + Math.random() * 0.004,
        color: STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (Math.random() < 0.02 && particles.length < 12) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 8;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.4 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [deals]);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, deal) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = "move";
    e.target.classList.add("opacity-50");
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("opacity-50");
    setDraggedDeal(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (!draggedDeal || draggedDeal.pipeline_stage === newStage) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/deals/${draggedDeal.deal_id}`,
        { pipeline_stage: newStage },
        { withCredentials: true }
      );
      
      setDeals(prev => prev.map(d => 
        d.deal_id === draggedDeal.deal_id 
          ? { ...d, pipeline_stage: newStage }
          : d
      ));
      
      toast.success(`Moved to ${STAGES.find(s => s.key === newStage)?.label}`);
    } catch (error) {
      toast.error("Failed to update deal");
    }
  };

  const handleToggleNetwork = async (dealId, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = currentStatus === "live" ? "hidden" : "live";
    try {
      await axios.put(`${BACKEND_URL}/api/deals/${dealId}/marketplace-status`,
        { status: newStatus }, { withCredentials: true });
      setDeals(prev => prev.map(d => d.deal_id === dealId ? { ...d, marketplace_status: newStatus } : d));
      toast.success(newStatus === "live" ? "Deal is now LIVE on the Network!" : "Deal removed from network");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update network status");
    }
  };

  const getDealsForStage = (stage) => {
    return deals.filter(d => d.pipeline_stage === stage);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate metrics
  const totalCapital = deals.reduce((sum, d) => sum + (d.asking_price || 0), 0);
  const activeDeals = deals.filter(d => !['closed', 'dead', 'sold'].includes(d.pipeline_stage)).length;
  const avgVelocity = deals.length > 0 
    ? Math.round(deals.reduce((sum, d) => {
        const days = Math.floor((Date.now() - new Date(d.created_at).getTime()) / (1000 * 60 * 60 * 24));
        return sum + days;
      }, 0) / deals.length)
    : 0;

  // Get phase totals
  const phaseTotals = Object.entries(PHASE_CONFIG).map(([phase, config]) => {
    const stagesInPhase = STAGES.filter(s => s.phase === phase);
    const count = stagesInPhase.reduce((sum, s) => sum + getDealsForStage(s.key).length, 0);
    return { phase, ...config, count };
  });

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="pipeline-page">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-black/80">
          <div className="px-4 md:px-6 py-3 md:py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-4 md:gap-6">
                <div>
                  <h1 className="text-base md:text-lg font-semibold tracking-tight">
                    Execution <span className="text-[#D4AF37]">Flow</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em] hidden md:block">Visualize deal progression through every stage of execution.</p>
                </div>
                
                {/* Live Badge */}
                <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                  <div className="relative w-2 h-2">
                    <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                    <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                </div>
              </div>

              {/* Center: Key Metrics - Hidden on mobile */}
              <div className="hidden lg:flex items-center gap-8">
                <MetricPill label="Capital Flow" value={`$${(totalCapital / 1000000).toFixed(2)}M`} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Active" value={activeDeals} highlight />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Avg Velocity" value={`${avgVelocity}d`} />
                <div className="w-px h-10 bg-white/[0.04]" />
                <MetricPill label="Total" value={deals.length} />
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate('/deals')}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold text-sm rounded-xl hover:opacity-90 transition-opacity"
                  data-testid="add-deal-pipeline-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">New Transaction</span>
                  <span className="sm:hidden">New</span>
                </button>
                
                <div className="text-right hidden md:block">
                  <div className="font-mono text-lg text-white/70 tracking-tight">
                    {currentTime.toLocaleTimeString('en-US', { hour12: false })}
                  </div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">Eastern</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Phase Summary Strip */}
        <section className="border-b border-white/[0.06] bg-black relative">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          <div className="px-4 md:px-6 py-4 relative z-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Phase Distribution</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {phaseTotals.filter(p => p.phase !== 'terminal').map((phase, idx) => (
                <div key={phase.phase} className="flex items-center gap-2">
                  <div 
                    className="px-3 py-1.5 rounded border"
                    style={{ 
                      borderColor: `${phase.color}30`,
                      background: `${phase.color}10`
                    }}
                  >
                    <span 
                      className="text-[10px] font-semibold uppercase tracking-wider"
                      style={{ color: phase.color }}
                    >
                      {phase.label}
                    </span>
                    <span className="text-white font-mono text-sm ml-2">{phase.count}</span>
                  </div>
                  {idx < phaseTotals.length - 2 && (
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Kanban Board */}
        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : deals.length === 0 ? (
            <div className="text-center py-16 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl">
              <Target className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No Transactions in Flow</h3>
              <p className="text-sm text-white/50 mb-6">Add your first transaction to start managing execution</p>
              <button 
                onClick={() => navigate('/deals')}
                className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Add Transaction
              </button>
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 320px)" }}>
              {STAGES.map((stage) => {
                const stageDeals = getDealsForStage(stage.key);
                const isDropTarget = dragOverStage === stage.key;
                
                return (
                  <div
                    key={stage.key}
                    className="flex-shrink-0 w-[180px]"
                    onDragOver={(e) => handleDragOver(e, stage.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, stage.key)}
                  >
                    {/* Column Header */}
                    <div 
                      className="flex items-center gap-2 px-3 py-2.5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-t-md"
                      style={{ borderTopColor: stage.color, borderTopWidth: '2px' }}
                    >
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ background: stage.color }}
                      />
                      <h3 className="text-[10px] font-semibold text-white uppercase tracking-wider flex-1 truncate" title={stage.label}>
                        {stage.label}
                      </h3>
                      <span 
                        className="font-mono text-xs px-1.5 py-0.5 rounded"
                        style={{ 
                          color: stage.color,
                          background: `${stage.color}15`
                        }}
                      >
                        {stageDeals.length}
                      </span>
                    </div>
                    
                    {/* Column Content */}
                    <div 
                      className={`p-2 space-y-2 bg-black border border-t-0 border-white/[0.06] rounded-b-md min-h-[400px] transition-all ${
                        isDropTarget ? 'bg-[#D4AF37]/[0.05] border-[#D4AF37]/30 border-dashed' : ''
                      }`}
                    >
                      {stageDeals.map((deal) => (
                        <KanbanCard 
                          key={deal.deal_id}
                          deal={deal}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onToggleNetwork={handleToggleNetwork}
                        />
                      ))}
                      
                      {stageDeals.length === 0 && !isDropTarget && (
                        <div className="text-center py-8">
                          <p className="text-[10px] text-white/40">Drop here</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Components

function MetricPill({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[9px] font-medium text-white/50 uppercase tracking-[0.12em] mb-1">{label}</div>
      <span className={`text-xl font-light tracking-tight ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
        {value}
      </span>
    </div>
  );
}

function KanbanCard({ deal, onDragStart, onDragEnd, onToggleNetwork }) {
  const spread = (deal.arv || 0) - (deal.asking_price || 0) - (deal.rehab_estimate || 0);
  const photo = deal.photos?.[0]
    ? (deal.photos[0].startsWith('http') ? deal.photos[0] : `${BACKEND_URL}${deal.photos[0]}`)
    : getPropertyPhoto(deal.property_type);
  const isLive = deal.marketplace_status === "live";

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden hover:border-[#D4AF37]/30 cursor-grab active:cursor-grabbing transition-all duration-300 group"
      data-testid={`pipeline-card-${deal.deal_id}`}
    >
      {/* Photo strip */}
      <div className="h-20 overflow-hidden relative">
        <img src={photo} alt={deal.property_address}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        <div className="absolute top-1.5 left-1.5">
          <GripVertical className="w-3 h-3 text-white/40 group-hover:text-white/70" />
        </div>
        {/* Network status badge on photo */}
        {isLive && (
          <div className="absolute top-1.5 right-1.5">
            <span className="flex items-center gap-0.5 text-[8px] font-bold text-[#00FF88] bg-black/70 backdrop-blur-sm px-1.5 py-0.5 rounded-full border border-emerald-500/40">
              <span className="w-1 h-1 rounded-full bg-[#00FF88] animate-pulse" />
              LIVE
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        <Link
          to={`/deals/${deal.deal_id}`}
          className="text-xs font-semibold text-white hover:text-[#D4AF37] truncate block transition-all duration-300 mb-1"
        >
          {deal.property_address?.split(',')[0] || 'Unknown'}
        </Link>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {deal.priority_score >= 70 && (
              <span className="text-[8px] font-semibold text-[#00FF88] bg-emerald-500/[0.1] px-1.5 py-0.5 rounded">
                HIGH
              </span>
            )}
            {spread > 0 && (
              <span className="text-[9px] font-mono text-[#00FF88]">
                +${(spread / 1000).toFixed(0)}K
              </span>
            )}
          </div>
          {/* Network toggle */}
          <button
            onClick={(e) => onToggleNetwork(deal.deal_id, deal.marketplace_status, e)}
            data-testid={`pipeline-network-toggle-${deal.deal_id}`}
            title={isLive ? "Remove from Network" : "Post to Network"}
            className={`text-[8px] font-bold px-1.5 py-0.5 rounded border transition-all ${
              isLive
                ? "text-[#00FF88] border-emerald-500/30 bg-emerald-500/10"
                : "text-white/25 border-white/[0.08] hover:text-white/50 hover:border-white/20"
            }`}
          >
            {isLive ? "LIVE" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}
